'use strict';
/**
 * Create an instance of `Parser` with
 * the given `string`, optionally passing
 * a `parent` name for namespacing methods
 *
 * ```js
 * const { Parser } = require('parse-code-context');
 * const parser = new Parser('function foo(a, b, c) {}');
 * ```
 * @name Parser
 * @param {String} `str`
 * @param {String} `parent`
 * @api public
 */

var _classCallCheck = require("/Users/klaushottvidal/DAPPS/remix-plugin-prettier/node_modules/babel-preset-react-app/node_modules/@babel/runtime/helpers/classCallCheck");

var _createClass = require("/Users/klaushottvidal/DAPPS/remix-plugin-prettier/node_modules/babel-preset-react-app/node_modules/@babel/runtime/helpers/createClass");

var Parser =
/*#__PURE__*/
function () {
  function Parser(parent) {
    _classCallCheck(this, Parser);

    this.parent = parent;
    this.fns = [];
    this.init();
  }
  /**
   * Convenience method for creating a property name
   * that is prefixed with the parent namespace, if defined.
   *
   * @name .name
   * @param {String} `name`
   * @return {String}
   * @api public
   */


  _createClass(Parser, [{
    key: "name",
    value: function name(_name) {
      return this.parent ? this.parent + (_name || '') : '';
    }
    /**
     * Register a parser to use (in addition to those already
     * registered as default parsers) with the given `regex` and
     * function.
     *
     * ```js
     * const parser = new Parser('function foo(a, b, c){}');
     *   .capture(/function\s*([\w$]+)\s*\(([^)]+)/, (match) => {
     *     return {
     *        name: match[1],
     *        params: matc(h[2] || '').split(/[,\s]/)
     *     };
     *   });
     * ```
     * @name .capture
     * @param {RegExp} `regex`
     * @param {Function} `fn`
     * @return {Object} The instance for chaining
     * @api public
     */

  }, {
    key: "capture",
    value: function capture(regex, fn) {
      this.fns.push({
        regex: regex,
        fn: fn
      });
      return this;
    }
    /**
     * Parse the string passed to the constructor with all registered parsers.
     *
     * @name .parse
     * @return {Object|Null}
     * @api public
     */

  }, {
    key: "parse",
    value: function parse(str, parent) {
      this.parent = parent || this.parent;
      var _iteratorNormalCompletion = true;
      var _didIteratorError = false;
      var _iteratorError = undefined;

      try {
        for (var _iterator = this.fns[Symbol.iterator](), _step; !(_iteratorNormalCompletion = (_step = _iterator.next()).done); _iteratorNormalCompletion = true) {
          var parser = _step.value;
          var re = parser.regex;
          var fn = parser.fn;
          var match = re.exec(str);

          if (match) {
            var ctx = fn.call(this, match, this.parent);

            if (ctx) {
              ctx.match = match;
              this.value = ctx;
              return ctx;
            }
          }
        }
      } catch (err) {
        _didIteratorError = true;
        _iteratorError = err;
      } finally {
        try {
          if (!_iteratorNormalCompletion && _iterator.return != null) {
            _iterator.return();
          }
        } finally {
          if (_didIteratorError) {
            throw _iteratorError;
          }
        }
      }
    }
  }, {
    key: "init",
    value: function init() {
      var _this = this;

      // module.exports method
      this.capture(/^(module\.exports)\s*=\s*function\s*\(([^)]+)/, function (m, parent) {
        return {
          type: 'method',
          receiver: m[1],
          name: '',
          params: params(m[2]),
          string: m[1] + '.' + m[2] + '()'
        };
      });
      this.capture(/^(module\.exports)\s*=\s*function\s([\w$]+)\s*\(([^)]+)/, function (m, parent) {
        return {
          type: 'function',
          subtype: 'expression',
          receiver: m[1],
          name: m[2],
          params: params(m[3]),
          string: m[2] + '()'
        };
      }); // class, possibly exported by name or as a default

      this.capture(/^\s*(export(\s+default)?\s+)?class\s+([\w$]+)(\s+extends\s+([\w$.]+(?:\(.*\))?))?\s*{/, function (m, parent) {
        return {
          type: 'class',
          ctor: m[3],
          name: m[3],
          extends: m[5],
          string: 'new ' + m[3] + '()'
        };
      }); // class constructor

      this.capture(/^\s*constructor\s*\(([^)]+)/, function (m, parent) {
        return {
          type: 'constructor',
          ctor: _this.parent,
          name: 'constructor',
          params: params(m[4]),
          string: _this.name('.prototype.') + 'constructor()'
        };
      }); // class method

      this.capture(/^\s*(static)?\s*(\*?)\s*(\[Symbol\.[^\]]+\]|[\w$]+|\[.*\])\s*\(([^)]*)/, function (m, parent) {
        return {
          type: 'method',
          ctor: _this.parent,
          name: m[2] + m[3],
          params: params(m[4]),
          static: m[1] === 'static',
          generator: m[2] === '*',
          string: _this.name(m[1] ? '.' : '.prototype.') + m[2] + m[3] + '()'
        };
      }); // named function statement, possibly exported by name or as a default

      this.capture(/^\s*(export(\s+default)?\s+)?function\s+([\w$]+)\s*\(([^)]+)/, function (m, parent) {
        return {
          type: 'function',
          subtype: 'statement',
          name: m[3],
          params: params(m[4]),
          string: m[3] + '()'
        };
      }); // anonymous function expression exported as a default

      this.capture(/^\s*export\s+default\s+function\s*\(([^)]+)/, function (m, parent) {
        return {
          type: 'function',
          name: m[1],
          // undefined
          params: params(m[4]),
          string: m[1] + '()'
        };
      }); // function expression

      this.capture(/^return\s+function(?:\s+([\w$]+))?\s*\(([^)]+)/, function (m, parent) {
        return {
          type: 'function',
          subtype: 'expression',
          name: m[1],
          params: params(m[4]),
          string: m[1] + '()'
        };
      }); // function expression

      this.capture(/^\s*(?:const|let|var)\s+([\w$]+)\s*=\s*function\s*\(([^)]+)/, function (m, parent) {
        return {
          type: 'function',
          subtype: 'expression',
          name: m[1],
          params: params(m[2]),
          string: (m[1] || '') + '()'
        };
      }); // prototype method

      this.capture(/^\s*([\w$.]+)\s*\.\s*prototype\s*\.\s*([\w$]+)\s*=\s*function\s*\(([^)]+)/, function (m, parent) {
        return {
          type: 'prototype method',
          category: 'method',
          ctor: m[1],
          name: m[2],
          params: params(m[3]),
          string: m[1] + '.prototype.' + m[2] + '()'
        };
      }); // prototype property

      this.capture(/^\s*([\w$.]+)\s*\.\s*prototype\s*\.\s*([\w$]+)\s*=\s*([^\n;]+)/, function (m, parent) {
        return {
          type: 'prototype property',
          ctor: m[1],
          name: m[2],
          value: trim(m[3]),
          string: m[1] + '.prototype.' + m[2]
        };
      }); // prototype property without assignment

      this.capture(/^\s*([\w$]+)\s*\.\s*prototype\s*\.\s*([\w$]+)\s*/, function (m, parent) {
        return {
          type: 'prototype property',
          ctor: m[1],
          name: m[2],
          string: m[1] + '.prototype.' + m[2]
        };
      }); // inline prototype

      this.capture(/^\s*([\w$.]+)\s*\.\s*prototype\s*=\s*{/, function (m, parent) {
        return {
          type: 'prototype',
          ctor: m[1],
          name: m[1],
          string: m[1] + '.prototype'
        };
      }); // Fat arrow function

      this.capture(/^\s*\(*\s*([\w$.]+)\s*\)*\s*=>/, function (m, parent) {
        return {
          type: 'function',
          ctor: _this.parent,
          name: m[1],
          string: _this.name('.prototype.') + m[1] + '()'
        };
      }); // inline method

      this.capture(/^\s*([\w$.]+)\s*:\s*function\s*\(([^)]+)/, function (m, parent) {
        return {
          type: 'method',
          ctor: _this.parent,
          name: m[1],
          string: _this.name('.prototype.') + m[1] + '()'
        };
      }); // inline property

      this.capture(/^\s*([\w$.]+)\s*:\s*([^\n;]+)/, function (m, parent) {
        return {
          type: 'property',
          ctor: _this.parent,
          name: m[1],
          value: trim(m[2]),
          string: _this.name('.') + m[1]
        };
      }); // inline getter/setter

      this.capture(/^\s*(get|set)\s*([\w$.]+)\s*\(([^)]+)/, function (m, parent) {
        return {
          type: 'property',
          ctor: _this.parent,
          name: m[2],
          string: _this.name('.prototype.') + m[2]
        };
      }); // method

      this.capture(/^\s*([\w$.]+)\s*\.\s*([\w$]+)\s*=\s*function\s*\(([^)]+)/, function (m, parent) {
        return {
          type: 'method',
          receiver: m[1],
          name: m[2],
          params: params(m[3]),
          string: m[1] + '.' + m[2] + '()'
        };
      }); // property

      this.capture(/^\s*([\w$.]+)\s*\.\s*([\w$]+)\s*=\s*([^\n;]+)/, function (m, parent) {
        return {
          type: 'property',
          receiver: m[1],
          name: m[2],
          value: trim(m[3]),
          string: m[1] + '.' + m[2]
        };
      }); // declaration

      this.capture(/^\s*(?:const|let|var)\s+([\w$]+)\s*=\s*([^\n;]+)/, function (m, parent) {
        return {
          type: 'declaration',
          name: m[1],
          value: trim(m[2]),
          string: m[1]
        };
      });
    }
  }]);

  return Parser;
}();

function params(val) {
  return trim(val).split(/[\s,]+/);
}

function trim(str) {
  return toString(str).trim();
}

function toString(str) {
  return str ? str.toString() : '';
}
/**
 * Expose `parse`
 */


var parse = function parse(str, options) {
  var parser = new Parser(options);
  return parser.parse(str);
};

parse.Parser = Parser;
module.exports = parse;